/* eslint-disable react/prop-types */
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Card, CardBody, Container, Row, Col, Alert, Button, Spinner } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState as TanStackSortingState,
} from '@tanstack/react-table';

import {
  DataTableProps,
  DataTableColumn,
  PaginatedResponse,
  TableQueryParams,
} from 'types/table.types';
import { useTableState } from 'hooks/useTableState';
import { useDebounce } from 'hooks/useDebounce';
import { DataTableApiService } from 'services/api/tableApi';
import { TableHeader } from './TableHeader';
import { TablePagination } from './TablePagination';
import { ColumnSelector } from './ColumnSelector';
import { TableSkeleton } from './TableSkeleton';
import { convertSortingToBackendFormat } from 'utils/tableHelpers';

export const DataTable = React.forwardRef<HTMLDivElement, DataTableProps<any>>(
  (
    {
      columns,
      data,
      queryKey,
      enableServerSide = true,
      onFetchData,
      enableSearch = true,
      enableFiltering = true,
      enablePagination = true,
      enableSorting = true,
      enableColumnVisibility = true,
      enableColumnReorder = false,
      enableRowSelection = false,
      enableBulkActions = false,
      enableVirtualization = false,
      enableExport = false,
      pageSizeOptions = [10, 20, 50, 100],
      defaultPageSize = 20,
      columnOrderStorageKey,
      columnVisibilityStorageKey,
      onRowClick,
      onSelectionChange,
      title,
      emptyMessage = 'No data available',
      loadingMessage = 'Loading...',
      errorMessage = 'Failed to load data',
      onRetry,
      bulkActions = [],
      striped = true,
      hover = true,
      bordered = true,
      responsive = true,
      dark = false,
      getRowId = (row, index) => String(index),
      className = '',
      containerClassName = '',
      toolbarExtra,
    },
    ref,
  ) => {
    // Table state management
    const tableState = useTableState({
      defaultPageSize,
      enableColumnVisibility,
      enableColumnReorder,
      columnVisibilityStorageKey,
      columnOrderStorageKey,
      columns,
    });

    // Debounced search
    const debouncedSearch = useDebounce(tableState.state.globalFilter, 500);

    // Build query params
    const queryParams = useMemo(() => {
      const params: TableQueryParams = tableState.buildQueryParams();
      params.search = debouncedSearch;
      return params;
    }, [tableState, debouncedSearch]);

    // Fetch data
    const {
      data: paginatedData,
      isLoading,
      isError,
      error,
      refetch,
    } = useQuery({
      queryKey: [queryKey, queryParams],
      queryFn: async () => {
        if (onFetchData) {
          return onFetchData(queryParams);
        }
        return DataTableApiService.fetchPaginatedData(queryKey, queryParams);
      },
      enabled: enableServerSide,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Prepare table data
    const tableData = useMemo(() => {
      if (enableServerSide && paginatedData) {
        return paginatedData.items || [];
      }
      return data || [];
    }, [enableServerSide, paginatedData, data]);

    // Prepare columns with visibility
    const visibleColumns = useMemo(() => {
      return columns.filter((col) => {
        const key = (col.accessorKey as string) || (col.id as string) || '';
        return tableState.state.columnVisibility[key] !== false;
      });
    }, [columns, tableState.state.columnVisibility]);

    // Convert to TanStack columns
    const tanStackColumns: ColumnDef<any>[] = useMemo(() => {
      return visibleColumns.map((col) => ({
        ...col,
        id: (col.accessorKey as string) || (col.id as string) || '',
      }));
    }, [visibleColumns]);

    // Initialize table instance
    const table = useReactTable({
      data: tableData,
      columns: tanStackColumns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      state: {
        sorting: tableState.state.sorting as TanStackSortingState,
        columnVisibility: tableState.state.columnVisibility,
        rowSelection: tableState.state.rowSelection,
      },
      onSortingChange: (updater) => {
        const newSorting =
          typeof updater === 'function' ? updater(tableState.state.sorting) : updater;
        tableState.setSortingState(newSorting);
      },
      onColumnVisibilityChange: (updater) => {
        const newVisibility =
          typeof updater === 'function' ? updater(tableState.state.columnVisibility) : updater;
        tableState.setColumnVisibility(newVisibility);
      },
      onRowSelectionChange: (updater) => {
        const newSelection =
          typeof updater === 'function' ? updater(tableState.state.rowSelection) : updater;
        tableState.setRowSelection(newSelection);
        onSelectionChange?.(Object.keys(newSelection).filter((key) => newSelection[key]));
      },
      manualPagination: enableServerSide,
      manualSorting: enableServerSide,
      manualFiltering: enableServerSide,
      pageCount: paginatedData?.totalPages,
    });

    // Handle export
    const handleExport = useCallback(async () => {
      try {
        const columnNames = visibleColumns
          .map((col) => (col.accessorKey as string) || '')
          .filter(Boolean);

        // For server-side, fetch all data
        if (enableServerSide) {
          const allData = await DataTableApiService.fetchPaginatedData(queryKey, {
            ...queryParams,
            pageSize: 10000, // Get max records
          });
          const csv = [
            columnNames.join(','),
            ...allData.items.map((row: any) =>
              columnNames.map((col) => JSON.stringify(row[col] ?? '')).join(','),
            ),
          ].join('\n');

          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `${queryKey}-export.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (err) {
        console.error('Export failed:', err);
      }
    }, [enableServerSide, queryKey, queryParams, visibleColumns]);

    // Selected rows
    const selectedRows = useMemo(() => {
      return Object.keys(tableState.state.rowSelection).filter(
        (key) => tableState.state.rowSelection[key],
      );
    }, [tableState.state.rowSelection]);

    // Row headers for table display
    const headers = useMemo(() => {
      return (
        table.getHeaderGroups()[0]?.headers.map((header) => ({
          id: header.id,
          label: header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext()),
          enableSorting: header.column.columnDef.enableSorting !== false,
          onSort: () =>
            header.column.columnDef.enableSorting &&
            header.column.toggleSorting(header.column.getIsSorted() === 'asc' ? true : undefined),
        })) || []
      );
    }, [table]);

    // Rows for table display
    const rows = useMemo(() => {
      return table.getRowModel().rows.map((row) => ({
        id: getRowId(row.original, row.index),
        data: row.original,
        isSelected: row.getIsSelected(),
        onSelect: () => row.toggleSelected(),
      }));
    }, [table, getRowId]);

    return (
      <div ref={ref} className={`data-table ${containerClassName}`.trim()}>
        {title && (
          <div className="mb-4">
            <h5 className="card-title">{title}</h5>
          </div>
        )}

        <Card className={className}>
          <CardBody>
            {/* Header Controls */}
            {(enableSearch || enableColumnVisibility || enableBulkActions) && (
              <Row className="mb-3">
                <Col md={12}>
                  {enableSearch && (
                    <TableHeader
                      columns={columns}
                      search={tableState.state.globalFilter}
                      onSearchChange={tableState.setGlobalFilter}
                      filters={tableState.state.columnFilters}
                      onFilterChange={(id, value) => {
                        tableState.addColumnFilter({ id, value });
                      }}
                      onRemoveFilter={tableState.removeColumnFilter}
                      onClearFilters={tableState.clearFilters}
                      enableSearch={enableSearch}
                      enableFiltering={enableFiltering}
                      enableColumnVisibility={enableColumnVisibility}
                      enableExport={enableExport}
                      enableBulkActions={enableBulkActions}
                      visibleColumns={visibleColumns}
                      onExport={handleExport}
                      isLoading={isLoading}
                      columnVisibility={tableState.state.columnVisibility}
                      onColumnVisibilityChange={tableState.setColumnVisibility}
                      onColumnToggle={tableState.toggleColumnVisibility}
                      selectedRows={selectedRows}
                      bulkActions={bulkActions}
                      toolbarExtra={toolbarExtra}
                    />
                  )}
                </Col>
              </Row>
            )}

            {/* Error State */}
            {isError && (
              <Alert color="danger" className="d-flex align-items-center gap-2">
                <i className="ri-error-warning-line"></i>
                <div>
                  <strong>Error:</strong> {errorMessage}
                </div>
                {onRetry && (
                  <Button size="sm" color="danger" outline onClick={onRetry} className="ms-auto">
                    Retry
                  </Button>
                )}
              </Alert>
            )}

            {/* Loading State */}
            {isLoading ? (
              <>
                <TableSkeleton
                  columns={visibleColumns.length}
                  rows={defaultPageSize}
                  enableRowSelection={enableRowSelection}
                />
              </>
            ) : tableData.length === 0 ? (
              <div className="text-center py-5">
                <i className="ri-inbox-2-line" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                <p className="text-muted mt-2">{emptyMessage}</p>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className={responsive ? 'table-responsive' : ''}>
                  <table
                    className={`table ${striped ? 'table-striped' : ''} ${
                      bordered ? 'table-bordered' : ''
                    } ${hover ? 'table-hover' : ''} ${
                      dark ? 'table-dark' : ''
                    } table-nowrap align-middle mb-0`.trim()}
                  >
                    <thead className="table-light">
                      {table.getHeaderGroups().map((headerGroup) => {
                        return (
                          <tr key={headerGroup.id}>
                            {enableRowSelection && (
                              <th style={{ width: '50px' }}>
                                <input
                                  type="checkbox"
                                  checked={table.getIsAllRowsSelected()}
                                  onChange={table.getToggleAllRowsSelectedHandler()}
                                  className="form-check-input"
                                />
                              </th>
                            )}
                            {headerGroup.headers.map((header) => {
                              const isSorted = header.column.getIsSorted();
                              return (
                                <th
                                  key={header.id}
                                  onClick={header.column.getToggleSortingHandler()}
                                  className={
                                    header.column.columnDef.enableSorting !== false
                                      ? 'cursor-pointer'
                                      : ''
                                  }
                                  style={{
                                    userSelect:
                                      header.column.columnDef.enableSorting !== false
                                        ? 'none'
                                        : 'auto',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    {header.isPlaceholder
                                      ? null
                                      : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        )}
                                    {header.column.columnDef.enableSorting !== false && (
                                      <span>
                                        {isSorted === 'asc' && <i className="ri-arrow-up-line"></i>}
                                        {isSorted === 'desc' && (
                                          <i className="ri-arrow-down-line"></i>
                                        )}
                                        {!isSorted && (
                                          <i className="ri-arrow-up-down-line text-muted"></i>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          onClick={() => onRowClick?.(row.original)}
                          className={row.getIsSelected() ? 'table-active' : ''}
                          style={{
                            cursor: onRowClick ? 'pointer' : 'auto',
                          }}
                        >
                          {enableRowSelection && (
                            <td>
                              <input
                                type="checkbox"
                                checked={row.getIsSelected()}
                                onChange={row.getToggleSelectedHandler()}
                                className="form-check-input"
                              />
                            </td>
                          )}
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Pagination */}
            {enablePagination && paginatedData && (
              <TablePagination
                pageNumber={paginatedData.pageNumber}
                pageSize={paginatedData.pageSize}
                totalCount={paginatedData.totalCount}
                totalPages={paginatedData.totalPages}
                hasNextPage={paginatedData.hasNextPage}
                hasPreviousPage={paginatedData.hasPreviousPage}
                onPageChange={tableState.goToPage}
                onPageSizeChange={tableState.setPageSize}
                pageSizeOptions={pageSizeOptions}
                isLoading={isLoading}
              />
            )}
          </CardBody>
        </Card>
      </div>
    );
  },
);

DataTable.displayName = 'DataTable';
